<script lang="ts" setup>
import { onMounted, reactive, watch } from 'vue'
import { useRouter } from 'vue-router'
import { useAxios } from '@/composables/useAxios'
import { handleAxiosError } from '@/composables/useAxiosError'
import { usePagination } from '@/composables'
import { splitStatusClass } from '@/composables/useAccountSplitStatus'
import { STATUS_CODES } from '@/constants'
import { formatTimestampSeconds } from '@/utils'
import {
  AccountSplitListItem,
  type AccountSplitListItem as AccountSplitListItemType,
  type PaginatedResponse
} from '@/types'
import BaseDashboardPage from '@/components/base/BaseDashboardPage.vue'

defineOptions({ name: 'AccountSplitHome' })

const router = useRouter()

const pagination = usePagination({ initialItemsPerPage: 50 })

const data = reactive({
  loading: false,
  splits: [] as AccountSplitListItemType[]
})

const loadSplits = () => {
  data.loading = true
  const { limit, offset } = pagination.buildQueryParams()
  useAxios<null, PaginatedResponse<AccountSplitListItemType>>(
    'GET',
    `/admin/account-splits?limit=${limit}&offset=${offset}`,
    true
  )
    .then((res) => {
      if (!res) return
      if (res.status === STATUS_CODES.OK && res.data) {
        const validate = AccountSplitListItem.array().safeParse(res.data.data)
        if (validate.success) {
          data.splits = validate.data
        } else {
          // API is trusted — fall back to raw data
          data.splits = (res.data.data || []) as AccountSplitListItemType[]
        }
        pagination.setTotal(res.data.total)
      }
    })
    .catch((error) => {
      handleAxiosError(error as Error, { logPrefix: 'AccountSplitHome:load' })
    })
    .finally(() => {
      data.loading = false
    })
}

watch(() => pagination.page.value, loadSplits)

const handleCreate = () => {
  router.push({ name: 'dashboardAdminAccountSplitNew' })
}

const openSplit = (id: number) => {
  router.push({ name: 'dashboardAdminAccountSplitDetail', params: { id } })
}

onMounted(loadSplits)
</script>

<template>
  <BaseDashboardPage
    :title="$t('dashboard.admin.accountSplit.title')"
    :subtitle="$t('dashboard.admin.accountSplit.subtitle')"
    icon="mdi-call-split"
    icon-color="purple"
  >
    <div class="section-card">
      <div class="section-header">
        <h3 class="section-title">{{ $t('dashboard.admin.accountSplit.title') }}</h3>
        <button class="btn-create" @click="handleCreate">
          <v-icon icon="mdi-plus" size="18" />
          <span>{{ $t('dashboard.admin.accountSplit.actions.create') }}</span>
        </button>
      </div>

      <!-- Loading -->
      <div v-if="data.loading" class="state-box">
        <v-progress-circular indeterminate size="32" width="3" color="#00B0FF" />
      </div>

      <!-- Empty -->
      <div v-else-if="data.splits.length === 0" class="state-box">
        <v-icon icon="mdi-call-split" size="48" class="empty-icon" />
        <p class="empty-title">{{ $t('dashboard.admin.accountSplit.home.emptyTitle') }}</p>
        <p class="empty-hint">{{ $t('dashboard.admin.accountSplit.home.emptyHint') }}</p>
      </div>

      <!-- Table -->
      <template v-else>
        <div class="data-table">
          <div class="table-header">
            <span class="col col-id">{{ $t('dashboard.admin.accountSplit.home.columnId') }}</span>
            <span class="col col-source">{{ $t('dashboard.admin.accountSplit.home.columnSource') }}</span>
            <span class="col col-target">{{ $t('dashboard.admin.accountSplit.home.columnTarget') }}</span>
            <span class="col col-status">{{ $t('dashboard.admin.accountSplit.home.columnStatus') }}</span>
            <span class="col col-created">{{ $t('dashboard.admin.accountSplit.home.columnCreated') }}</span>
            <span class="col col-actions"></span>
          </div>
          <div
            v-for="split in data.splits"
            :key="split.id"
            class="table-row"
            @click="openSplit(split.id)"
          >
            <span class="col col-id mono">#{{ split.id }}</span>
            <span class="col col-source mono">{{ split.source_account?.email || `#${split.source_account?.id ?? '?'}` }}</span>
            <span class="col col-target mono">
              <template v-if="split.target_account">{{ split.target_account.email || `#${split.target_account.id}` }}</template>
              <template v-else>—</template>
            </span>
            <span class="col col-status">
              <span class="status-chip" :class="splitStatusClass(split.status)">
                {{ $t(`dashboard.admin.accountSplit.status.${split.status}`) }}
              </span>
            </span>
            <span class="col col-created">{{ formatTimestampSeconds(split.created) }}</span>
            <span class="col col-actions">
              <button class="row-link" @click.stop="openSplit(split.id)">
                {{ $t('dashboard.admin.accountSplit.actions.open') }}
                <v-icon icon="mdi-chevron-right" size="16" />
              </button>
            </span>
          </div>
        </div>

        <!-- Pagination -->
        <div v-if="pagination.totalPages.value > 1" class="pagination-controls">
          <div class="pagination-info">
            {{ pagination.total.value }} total
          </div>
          <div class="pagination-buttons">
            <button
              class="pagination-btn"
              :disabled="pagination.page.value === 1"
              @click="pagination.goToPage(1)"
            >
              <v-icon icon="mdi-chevron-double-left" size="18" />
            </button>
            <button
              class="pagination-btn"
              :disabled="pagination.page.value === 1"
              @click="pagination.prevPage()"
            >
              <v-icon icon="mdi-chevron-left" size="18" />
            </button>
            <span class="page-indicator">
              {{ pagination.page.value }} / {{ pagination.totalPages.value }}
            </span>
            <button
              class="pagination-btn"
              :disabled="pagination.page.value === pagination.totalPages.value"
              @click="pagination.nextPage()"
            >
              <v-icon icon="mdi-chevron-right" size="18" />
            </button>
            <button
              class="pagination-btn"
              :disabled="pagination.page.value === pagination.totalPages.value"
              @click="pagination.goToPage(pagination.totalPages.value)"
            >
              <v-icon icon="mdi-chevron-double-right" size="18" />
            </button>
          </div>
        </div>
      </template>
    </div>
  </BaseDashboardPage>
</template>

<style lang="scss" scoped>
.section-card {
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid rgba(120, 163, 255, 0.08);
  border-radius: 16px;
  padding: 24px;
}

.section-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 20px;
  gap: 12px;
  flex-wrap: wrap;
}

.section-title {
  font-size: 1.1rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.9);
  margin: 0;
}

.btn-create {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  background: linear-gradient(135deg, #00B0FF, #1FE2D4);
  border: none;
  border-radius: 8px;
  color: white;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    box-shadow: 0 4px 12px rgba(0, 176, 255, 0.3);
  }
}

.state-box {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 48px 24px;
  text-align: center;
}

.empty-icon {
  color: rgba(200, 218, 255, 0.25);
  margin-bottom: 12px;
}

.empty-title {
  font-size: 1rem;
  font-weight: 500;
  color: rgba(200, 218, 255, 0.6);
  margin: 0 0 4px;
}

.empty-hint {
  font-size: 0.85rem;
  color: rgba(200, 218, 255, 0.4);
  margin: 0;
}

.data-table {
  overflow-x: auto;
}

.table-header,
.table-row {
  display: grid;
  grid-template-columns: 80px minmax(0, 1fr) minmax(0, 1fr) 160px 180px 100px;
  gap: 12px;
  align-items: center;
  padding: 12px 14px;
}

.table-header {
  border-bottom: 1px solid rgba(120, 163, 255, 0.1);
  font-size: 0.75rem;
  font-weight: 600;
  color: rgba(200, 218, 255, 0.5);
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.table-row {
  border-bottom: 1px solid rgba(120, 163, 255, 0.05);
  font-size: 0.9rem;
  color: rgba(255, 255, 255, 0.85);
  cursor: pointer;
  transition: background 0.15s ease;

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background: rgba(120, 163, 255, 0.04);
  }
}

.mono {
  font-family: 'JetBrains Mono', 'Fira Code', monospace;
  font-size: 0.85rem;
}

.status-chip {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 6px;
  font-size: 0.78rem;
  font-weight: 500;
}

.status-success {
  background: rgba(129, 199, 132, 0.1);
  border: 1px solid rgba(129, 199, 132, 0.3);
  color: #81c784;
}

.status-warning {
  background: rgba(255, 183, 77, 0.1);
  border: 1px solid rgba(255, 183, 77, 0.3);
  color: #ffb74d;
}

.status-error {
  background: rgba(244, 67, 54, 0.1);
  border: 1px solid rgba(244, 67, 54, 0.3);
  color: #ef5350;
}

.status-info {
  background: rgba(0, 176, 255, 0.1);
  border: 1px solid rgba(0, 176, 255, 0.3);
  color: #4fc3f7;
}

.status-grey {
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  color: rgba(200, 218, 255, 0.5);
}

.row-link {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  background: transparent;
  border: 1px solid transparent;
  border-radius: 8px;
  padding: 4px 8px;
  color: #4fc3f7;
  font-size: 0.82rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    background: rgba(0, 176, 255, 0.08);
    border-color: rgba(0, 176, 255, 0.2);
  }
}

.pagination-controls {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-top: 16px;
  margin-top: 12px;
  border-top: 1px solid rgba(120, 163, 255, 0.08);
}

.pagination-info {
  font-size: 0.85rem;
  color: rgba(200, 218, 255, 0.6);
}

.pagination-buttons {
  display: flex;
  align-items: center;
  gap: 8px;
}

.pagination-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(120, 163, 255, 0.15);
  border-radius: 8px;
  color: rgba(200, 218, 255, 0.7);
  cursor: pointer;

  &:hover:not(:disabled) {
    background: rgba(0, 176, 255, 0.1);
    border-color: rgba(0, 176, 255, 0.25);
    color: #00B0FF;
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
}

.page-indicator {
  font-size: 0.9rem;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.9);
  padding: 0 12px;
  min-width: 70px;
  text-align: center;
}

@media (max-width: 900px) {
  .table-header,
  .table-row {
    grid-template-columns: 60px 1fr 1fr 130px;
  }
  .col-created,
  .col-actions {
    display: none;
  }
}
</style>
